import type {
  FeaturedAwardShow,
  FeaturedShowBallot,
} from "@award-party/shared";
import { getSignUpHref } from "@/lib/auth-redirect";

export type HeroPreviewNominee = {
  name: string;
  subtitle: string;
  poster: string;
  selected: boolean;
};

export type HeroPreviewPlayer = {
  rank: number;
  name: string;
  initial: string;
  score: string;
  pct: number;
  bg: string;
  first: boolean;
};

export const HERO_PREVIEW_SHOW_KEYS = [
  "emmys",
  "oscars",
  "grammys",
  "tonys",
  "generic",
] as const;

export type HeroPreviewShowKey = (typeof HERO_PREVIEW_SHOW_KEYS)[number];

export const HERO_PREVIEW_REACTION_KINDS = [
  "tookTheLead",
  "plusOneCorrect",
  "picksLocked",
] as const;

export type HeroPreviewReactionKind =
  (typeof HERO_PREVIEW_REACTION_KINDS)[number];

export type HeroPreviewReaction = {
  name: string;
  initial: string;
  bg: string;
  kind: HeroPreviewReactionKind;
};

export type HeroPreviewContent = {
  showKey: HeroPreviewShowKey;
  showName: string;
  showYear: number;
  categoryName: string | null;
  categoryIndex: number;
  pickedCount: number;
  totalCategories: number;
  watchingCount: number;
  nominees: HeroPreviewNominee[];
  leaderboard: HeroPreviewPlayer[];
  reactions: HeroPreviewReaction[];
};

export type HeroPreviewYouIdentity = {
  name: string;
  initial: string;
  bg?: string;
};

export const HERO_PREVIEW_YOU_BG = "#E8C96A";

const HERO_PREVIEW_POSTERS = [
  "#5c1f24",
  "#2c3a4a",
  "#3d2f1e",
  "#1e3a32",
  "#3a2a48",
  "#1a2e4a",
] as const;
const HERO_PREVIEW_CATEGORY_INDEX = 4;
const HERO_PREVIEW_TOTAL_CATEGORIES = 23;
const HERO_PREVIEW_SELECTED_INDEX = 2;

const YOU_LEAD_MARGIN = 1;
const YOU_MISS_MARGIN = 3;
const LEADERBOARD_LIMIT = 4;
const SCORE_TOTAL_FALLBACK = 17;

export function heroPreviewShowKey(showName: string): HeroPreviewShowKey {
  if (showName === "Emmys") return "emmys";
  if (showName === "Grammys") return "grammys";
  if (showName === "Tonys") return "tonys";
  if (showName === "Oscars") return "oscars";
  return "generic";
}

const LEADERBOARD: HeroPreviewPlayer[] = [
  {
    rank: 1,
    name: "Maya",
    initial: "M",
    score: "15/17",
    pct: 88,
    bg: "#A8D5BA",
    first: true,
  },
  {
    rank: 2,
    name: "Alex",
    initial: "A",
    score: "13/17",
    pct: 76,
    bg: "#B4C4E8",
    first: false,
  },
  {
    rank: 3,
    name: "Jordan",
    initial: "J",
    score: "11/17",
    pct: 65,
    bg: "#F5D08A",
    first: false,
  },
  {
    rank: 4,
    name: "Sam",
    initial: "S",
    score: "9/17",
    pct: 53,
    bg: "#E8B4B8",
    first: false,
  },
];

type Ballot = {
  nominees: Omit<HeroPreviewNominee, "selected">[];
  selectedName: string;
};

const OSCAR_BALLOT: Ballot = {
  selectedName: "Sinners",
  nominees: [
    { name: "Sinners", subtitle: "Ryan Coogler", poster: "#5c1f24" },
    { name: "One Battle After Another", subtitle: "Paul Thomas Anderson", poster: "#2c3a4a" },
    { name: "Hamnet", subtitle: "Chloé Zhao", poster: "#3d2f1e" },
    { name: "Marty Supreme", subtitle: "Josh Safdie", poster: "#1e3a32" },
    { name: "Sentimental Value", subtitle: "Joachim Trier", poster: "#3a2a48" },
  ],
};

const BALLOTS: Record<string, Ballot> = {
  Emmys: {
    selectedName: "The Pitt",
    nominees: [
      { name: "The Pitt", subtitle: "Max", poster: "#4a1f1a" },
      { name: "Severance", subtitle: "Apple TV+", poster: "#1c2a38" },
      { name: "The White Lotus", subtitle: "HBO", poster: "#5a3a1a" },
      { name: "Slow Horses", subtitle: "Apple TV+", poster: "#2a3324" },
      { name: "Andor", subtitle: "Disney+", poster: "#1a2230" },
    ],
  },
  Oscars: OSCAR_BALLOT,
  Grammys: {
    selectedName: "HIT ME HARD AND SOFT",
    nominees: [
      { name: "HIT ME HARD AND SOFT", subtitle: "Billie Eilish", poster: "#1a2e4a" },
      { name: "Short n' Sweet", subtitle: "Sabrina Carpenter", poster: "#5a3040" },
      { name: "Cowboy Carter", subtitle: "Beyoncé", poster: "#4a3218" },
      { name: "The Secret of Us", subtitle: "Gracie Abrams", poster: "#2a3840" },
      { name: "GNX", subtitle: "Kendrick Lamar", poster: "#241818" },
    ],
  },
  Tonys: {
    selectedName: "Maybe Happy Ending",
    nominees: [
      { name: "Maybe Happy Ending", subtitle: "Belasco Theatre", poster: "#2a3048" },
      { name: "Death Becomes Her", subtitle: "Lunt-Fontanne", poster: "#4a2030" },
      { name: "Sunset Blvd.", subtitle: "St. James Theatre", poster: "#3a2818" },
      { name: "Buena Vista Social Club", subtitle: "Schoenfeld", poster: "#1e3a2e" },
      { name: "Gypsy", subtitle: "Majestic Theatre", poster: "#402818" },
    ],
  },
};

function withSelection(ballot: Ballot): HeroPreviewNominee[] {
  return ballot.nominees.map((nominee) => ({
    ...nominee,
    selected: nominee.name === ballot.selectedName,
  }));
}

export function heroPreviewMonogram(name: string): string {
  const match = name.match(/[\p{L}\p{N}]/u);
  return (match?.[0] ?? "").toLocaleUpperCase();
}

export function heroPreviewPickedCount(totalCategories: number): number {
  if (!Number.isFinite(totalCategories) || totalCategories <= 0) return 0;
  if (totalCategories === 1) return 1;
  return Math.min(
    totalCategories - 1,
    Math.max(1, Math.round(totalCategories * 0.7)),
  );
}

export function heroPreviewCreatePartyPath(showId: string | null): string {
  const trimmed = showId?.trim() ?? "";
  if (!trimmed) return "/parties/create";
  return `/parties/create?showId=${encodeURIComponent(trimmed)}`;
}

export function heroPreviewCreatePartyHref(
  createPartyPath: string,
  signedIn: boolean,
): string {
  return signedIn ? createPartyPath : getSignUpHref(createPartyPath);
}

function nomineesFromBallot(ballot: FeaturedShowBallot): HeroPreviewNominee[] {
  const selectedIndex = Math.min(
    HERO_PREVIEW_SELECTED_INDEX,
    ballot.nominees.length - 1,
  );

  return ballot.nominees.map((nominee, index) => ({
    name: nominee.name,
    subtitle: nominee.subtitle ?? "",
    poster: HERO_PREVIEW_POSTERS[index % HERO_PREVIEW_POSTERS.length] ?? "#2c3a4a",
    selected: index === selectedIndex,
  }));
}

export function getHeroPreviewContent(
  show: Pick<FeaturedAwardShow, "displayName" | "year"> & {
    ballot?: FeaturedShowBallot | null;
  },
): HeroPreviewContent {
  const liveBallot =
    show.ballot && show.ballot.nominees.length > 0 ? show.ballot : null;
  const ballot = liveBallot ? null : (BALLOTS[show.displayName] ?? OSCAR_BALLOT);
  const nominees = liveBallot
    ? nomineesFromBallot(liveBallot)
    : withSelection(ballot ?? OSCAR_BALLOT);
  const totalCategories =
    liveBallot?.totalCategories ?? HERO_PREVIEW_TOTAL_CATEGORIES;
  const maya = LEADERBOARD[0];
  const alex = LEADERBOARD[1];
  const jordan = LEADERBOARD[2];

  if (!maya || !alex || !jordan) {
    throw new Error("Hero preview leaderboard is incomplete");
  }

  return {
    showKey: heroPreviewShowKey(show.displayName),
    showName: show.displayName,
    showYear: show.year,
    categoryName: liveBallot?.categoryName ?? null,
    categoryIndex: liveBallot?.categoryIndex ?? HERO_PREVIEW_CATEGORY_INDEX,
    pickedCount: heroPreviewPickedCount(totalCategories),
    totalCategories,
    watchingCount: 8,
    nominees,
    leaderboard: LEADERBOARD,
    reactions: [
      {
        name: maya.name,
        initial: maya.initial,
        bg: maya.bg,
        kind: "tookTheLead",
      },
      {
        name: alex.name,
        initial: alex.initial,
        bg: alex.bg,
        kind: "plusOneCorrect",
      },
      {
        name: jordan.name,
        initial: jordan.initial,
        bg: jordan.bg,
        kind: "picksLocked",
      },
    ],
  };
}

export function heroPreviewAnnouncedName(
  nominees: HeroPreviewNominee[],
): string {
  return nominees.find((nominee) => nominee.selected)?.name ?? nominees[0]?.name ?? "";
}

export function selectHeroPreviewNominee(
  nominees: HeroPreviewNominee[],
  selectedName: string,
): HeroPreviewNominee[] {
  if (!nominees.some((nominee) => nominee.name === selectedName)) {
    return nominees;
  }

  return nominees.map((nominee) => ({
    ...nominee,
    selected: nominee.name === selectedName,
  }));
}

export function heroPreviewScoreValue(score: string): number {
  const raw = score.split("/")[0]?.trim() ?? "";
  const value = Number.parseInt(raw, 10);
  return Number.isFinite(value) ? value : 0;
}

function heroPreviewScoreTotal(score: string): number {
  const raw = score.split("/")[1]?.trim() ?? "";
  const value = Number.parseInt(raw, 10);
  return Number.isFinite(value) && value > 0 ? value : SCORE_TOTAL_FALLBACK;
}

function withHeroPreviewYouStanding(
  leaderboard: HeroPreviewPlayer[],
  options: { pickedWinner: boolean; you: HeroPreviewYouIdentity },
): HeroPreviewPlayer[] {
  const others = leaderboard.filter((row) => row.name !== options.you.name);
  const leaderScore = others.reduce(
    (max, row) => Math.max(max, heroPreviewScoreValue(row.score)),
    0,
  );
  const total = others[0]
    ? heroPreviewScoreTotal(others[0].score)
    : SCORE_TOTAL_FALLBACK;
  const correct = options.pickedWinner
    ? leaderScore + YOU_LEAD_MARGIN
    : Math.max(0, leaderScore - YOU_MISS_MARGIN);

  const youRow: HeroPreviewPlayer = {
    rank: 0,
    name: options.you.name,
    initial: options.you.initial,
    score: `${correct}/${total}`,
    pct: total > 0 ? Math.round((correct / total) * 100) : 0,
    bg: options.you.bg ?? HERO_PREVIEW_YOU_BG,
    first: false,
  };

  return [...others, youRow]
    .sort((a, b) => {
      const diff =
        heroPreviewScoreValue(b.score) - heroPreviewScoreValue(a.score);
      if (diff !== 0) return diff;
      if (a.name === options.you.name) return 1;
      if (b.name === options.you.name) return -1;
      return 0;
    })
    .slice(0, LEADERBOARD_LIMIT)
    .map((row, index) => ({
      ...row,
      rank: index + 1,
      first: index === 0,
    }));
}

export function getInteractiveHeroPreview(
  content: HeroPreviewContent,
  selectedName: string | null,
  you: HeroPreviewYouIdentity,
): Pick<HeroPreviewContent, "nominees" | "leaderboard"> {
  if (selectedName === null) {
    return {
      nominees: content.nominees,
      leaderboard: content.leaderboard,
    };
  }

  const nominees = selectHeroPreviewNominee(content.nominees, selectedName);
  const announcedName = heroPreviewAnnouncedName(content.nominees);
  const pickedName = nominees.find((nominee) => nominee.selected)?.name;
  const pickedWinner = pickedName === announcedName && announcedName.length > 0;

  return {
    nominees,
    leaderboard: withHeroPreviewYouStanding(content.leaderboard, {
      pickedWinner,
      you,
    }),
  };
}
